import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  FileText, 
  Download, 
  Trash2, 
  Plus, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Archive,
  Eye,
  Settings2,
  Sparkles,
  Link as LinkIcon,
  Globe,
  PenTool,
  Users,
  FileCode,
  List,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Form state
  const [keyword, setKeyword] = useState("");
  const [anchorLink, setAnchorLink] = useState("");
  const [anchorText, setAnchorText] = useState("");
  const [articleCount, setArticleCount] = useState("1");
  const [language, setLanguage] = useState("Indonesia");
  const [writingStyle, setWritingStyle] = useState("blog-friendly");
  const [tone, setTone] = useState("professional");

  // Bulk mode state
  const [mode, setMode] = useState("single"); // "single" | "bulk"
  const [bulkText, setBulkText] = useState("");
  const [bulkItems, setBulkItems] = useState([]); // parsed preview
  const [bulkError, setBulkError] = useState("");
  
  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedArticleIds, setSelectedArticleIds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Parse bulk textarea input
  const parseBulkText = useCallback((text) => {
    const lines = text.split("\n").map(l => l.trim()).filter(l => l && !l.startsWith("#"));
    const items = [];
    const errors = [];

    lines.forEach((line, i) => {
      // Support: keyword | anchor_url | anchor_text (anchor_text optional)
      const parts = line.split("|").map(p => p.trim());
      if (parts.length < 2) {
        errors.push(`Baris ${i + 1}: format salah (butuh minimal: keyword | url)`);
        return;
      }
      const [kw, url, ancText] = parts;
      if (!kw) { errors.push(`Baris ${i + 1}: keyword kosong`); return; }
      if (!url || !url.startsWith("http")) { errors.push(`Baris ${i + 1}: URL tidak valid`); return; }
      items.push({ keyword: kw, anchor_link: url, anchor_text: ancText || null });
    });

    setBulkItems(items);
    setBulkError(errors.length > 0 ? errors.join("\n") : "");
    return items;
  }, []);

  // Fetch articles history
  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Poll job status
  useEffect(() => {
    let interval;
    if (currentJob && currentJob.status === "processing") {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/articles/job/${currentJob.id}`);
          const jobData = response.data.job;
          
          setCurrentJob(prev => ({
            ...prev,
            ...jobData,
            article_count: prev.article_count,
          }));
          
          if (jobData.status === "completed" || jobData.status === "failed") {
            clearInterval(interval);
            setIsGenerating(false);
            
            if (jobData.status === "completed") {
              toast.success(`${jobData.articles_generated} artikel berhasil digenerate!`);
              fetchArticles();
            } else {
              const errMsg = jobData.error_message || "Gagal generate artikel. Silakan coba lagi.";
              const isPoinHabis = errMsg.includes("Poin API habis") || errMsg.includes("Insufficient points");
              if (isPoinHabis) {
                toast.error(`⚠️ Poin API habis! ${jobData.articles_generated} artikel berhasil, sisanya gagal. Silakan top up poin di use.api.co.id`, { duration: 8000 });
              } else {
                toast.error(errMsg);
              }
              fetchArticles();
            }
          }
        } catch (error) {
          console.error("Error polling job:", error);
        }
      }, 2000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentJob, fetchArticles]);

  // Handle form submit
  const handleGenerate = async () => {
    if (!keyword.trim()) {
      toast.error("Keyword harus diisi!");
      return;
    }
    if (!anchorLink.trim()) {
      toast.error("Anchor link harus diisi!");
      return;
    }

    setIsGenerating(true);
    setCurrentJob(null);

    try {
      const response = await axios.post(`${API}/articles/generate`, {
        keyword: keyword.trim(),
        anchor_link: anchorLink.trim(),
        anchor_text: anchorText.trim() || null,
        article_count: parseInt(articleCount),
        language,
        writing_style: [writingStyle],
        tone: [tone]
      });

      setCurrentJob({
        id: response.data.job_id,
        status: "processing",
        progress: 0,
        articles_generated: 0,
        article_count: parseInt(articleCount),
      });

      toast.info("Memulai generate artikel...");
    } catch (error) {
      setIsGenerating(false);
      toast.error(error.response?.data?.detail || "Gagal memulai generate artikel");
    }
  };

  // Handle bulk generate
  const handleBulkGenerate = async () => {
    const items = parseBulkText(bulkText);
    if (items.length === 0) {
      toast.error("Tidak ada item valid. Periksa format input.");
      return;
    }
    if (bulkError) {
      toast.error("Ada format yang salah. Perbaiki terlebih dahulu.");
      return;
    }

    setIsGenerating(true);
    setCurrentJob(null);

    try {
      const response = await axios.post(`${API}/articles/generate-bulk`, {
        items,
        language,
        writing_style: [writingStyle],
        tone: [tone],
      });

      setCurrentJob({
        id: response.data.job_id,
        status: "processing",
        progress: 0,
        articles_generated: 0,
        article_count: response.data.total,
        is_bulk: true,
        current_keyword: items[0]?.keyword || "",
      });

      toast.info(`Memulai bulk generate ${items.length} artikel...`);
    } catch (error) {
      setIsGenerating(false);
      toast.error(error.response?.data?.detail || "Gagal memulai bulk generate");
    }
  };

  // View article detail
  const handleViewArticle = async (articleId) => {
    try {
      const response = await axios.get(`${API}/articles/${articleId}`);
      setSelectedArticle(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error("Gagal memuat artikel");
    }
  };

  // Download single article
  const handleDownloadArticle = async (articleId) => {
    try {
      const response = await axios.get(`${API}/articles/${articleId}/download`, {
        responseType: "blob"
      });
      
      const article = articles.find(a => a.id === articleId);
      const filename = `${article?.title?.slice(0, 50).replace(/\s+/g, '_') || 'article'}.html`;
      
      saveAs(response.data, filename);
      toast.success("Artikel berhasil didownload!");
    } catch (error) {
      toast.error("Gagal download artikel");
    }
  };

  // Download selected articles as ZIP
  const handleDownloadZip = async () => {
    if (selectedArticleIds.length === 0) {
      toast.error("Pilih artikel yang ingin didownload!");
      return;
    }

    try {
      const response = await axios.post(`${API}/articles/download-zip`, selectedArticleIds, {
        responseType: "blob"
      });
      
      saveAs(response.data, `articles_${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success("ZIP berhasil didownload!");
      setSelectedArticleIds([]);
    } catch (error) {
      toast.error("Gagal download ZIP");
    }
  };

  // Delete article
  const handleDeleteArticle = async (articleId) => {
    try {
      await axios.delete(`${API}/articles/${articleId}`);
      toast.success("Artikel berhasil dihapus!");
      fetchArticles();
      if (selectedArticle?.id === articleId) {
        setSelectedArticle(null);
        setShowPreview(false);
      }
    } catch (error) {
      toast.error("Gagal menghapus artikel");
    }
  };

  // Delete selected articles (bulk)
  const handleDeleteBulk = async () => {
    if (selectedArticleIds.length === 0) {
      toast.error("Pilih artikel yang ingin dihapus!");
      return;
    }

    try {
      const response = await axios.post(`${API}/articles/delete-bulk`, selectedArticleIds);
      toast.success(response.data.message);
      setSelectedArticleIds([]);
      fetchArticles();
      if (selectedArticle && selectedArticleIds.includes(selectedArticle.id)) {
        setSelectedArticle(null);
        setShowPreview(false);
      }
    } catch (error) {
      toast.error("Gagal menghapus artikel");
    }
  };

  // Export to WordPress XML
  const handleExportWordPress = async () => {
    const idsToExport = selectedArticleIds.length > 0 ? selectedArticleIds : articles.map(a => a.id);
    
    if (idsToExport.length === 0) {
      toast.error("Tidak ada artikel untuk diexport!");
      return;
    }

    try {
      const response = await axios.post(`${API}/articles/export-wordpress`, idsToExport, {
        responseType: "blob"
      });
      
      saveAs(response.data, `wordpress_import_${new Date().toISOString().slice(0, 10)}.xml`);
      toast.success(`${idsToExport.length} artikel berhasil diexport ke WordPress XML!`);
    } catch (error) {
      toast.error("Gagal export ke WordPress");
    }
  };

  // Toggle article selection
  const toggleArticleSelection = (articleId) => {
    setSelectedArticleIds(prev => 
      prev.includes(articleId) 
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  // Select all articles
  const toggleSelectAll = () => {
    if (selectedArticleIds.length === articles.length) {
      setSelectedArticleIds([]);
    } else {
      setSelectedArticleIds(articles.map(a => a.id));
    }
  };

  return (
    <div className="app-container min-h-screen">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Article Generator</h1>
                <p className="text-sm text-slate-500">Generate SEO-optimized articles</p>
              </div>
            </div>
            
            {articles.length > 0 && (
              <Badge variant="secondary" className="text-sm">
                {articles.length} artikel
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Column - Form */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  Generate Artikel Baru
                </CardTitle>
                <CardDescription>
                  Mode tunggal atau bulk (banyak keyword sekaligus)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">

                <Tabs value={mode} onValueChange={setMode}>
                  <TabsList className="w-full">
                    <TabsTrigger value="single" className="flex-1">
                      <FileText className="w-4 h-4 mr-2" />
                      Single
                    </TabsTrigger>
                    <TabsTrigger value="bulk" className="flex-1">
                      <List className="w-4 h-4 mr-2" />
                      Bulk
                    </TabsTrigger>
                  </TabsList>

                  {/* ── SINGLE MODE ── */}
                  <TabsContent value="single" className="space-y-4 mt-4">
                    {/* Keyword */}
                    <div className="space-y-2">
                      <Label htmlFor="keyword" className="text-sm font-medium text-slate-700">
                        Keyword / Topik Artikel
                      </Label>
                      <Input
                        id="keyword"
                        data-testid="keyword-input"
                        placeholder="Contoh: Tips SEO untuk Website"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="h-11"
                      />
                    </div>

                    {/* Anchor Link */}
                    <div className="space-y-2">
                      <Label htmlFor="anchor-link" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <LinkIcon className="w-4 h-4" />
                        Anchor Link (URL)
                      </Label>
                      <Input
                        id="anchor-link"
                        data-testid="anchor-link-input"
                        placeholder="https://example.com/target-page"
                        value={anchorLink}
                        onChange={(e) => setAnchorLink(e.target.value)}
                        className="h-11"
                      />
                    </div>

                    {/* Anchor Text */}
                    <div className="space-y-2">
                      <Label htmlFor="anchor-text" className="text-sm font-medium text-slate-700">
                        Anchor Text (Opsional)
                      </Label>
                      <Input
                        id="anchor-text"
                        data-testid="anchor-text-input"
                        placeholder="Teks yang ditampilkan (default: keyword)"
                        value={anchorText}
                        onChange={(e) => setAnchorText(e.target.value)}
                        className="h-11"
                      />
                    </div>

                    {/* Article Count */}
                    <div className="space-y-2">
                      <Label htmlFor="article-count" className="text-sm font-medium text-slate-700">
                        Jumlah Artikel
                      </Label>
                      <Select value={articleCount} onValueChange={setArticleCount}>
                        <SelectTrigger data-testid="article-count-select" className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <SelectItem key={num} value={String(num)}>
                              {num} artikel
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>

                  {/* ── BULK MODE ── */}
                  <TabsContent value="bulk" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">
                        Daftar Keyword &amp; Anchor Link
                      </Label>
                      <p className="text-xs text-slate-500">
                        Format per baris: <code className="bg-slate-100 px-1 rounded">keyword | url | anchor_text(opsional)</code>
                      </p>
                      <Textarea
                        placeholder={`Tips SEO website | https://usix.com/seo | Tips SEO\nCara membuat blog | https://usix.com/blog\nResep kopi susu | https://usix.com/kopi | kopi susu enak`}
                        value={bulkText}
                        onChange={(e) => {
                          setBulkText(e.target.value);
                          parseBulkText(e.target.value);
                        }}
                        className="min-h-[180px] font-mono text-sm resize-y"
                      />
                    </div>

                    {/* Preview parsed items */}
                    {bulkItems.length > 0 && (
                      <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50">
                        <p className="text-xs font-medium text-slate-600">
                          ✅ {bulkItems.length} item valid — akan generate {bulkItems.length} artikel:
                        </p>
                        <ScrollArea className="max-h-[150px]">
                          <div className="space-y-1">
                            {bulkItems.map((item, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                <span className="font-mono text-blue-600 shrink-0">{i + 1}.</span>
                                <div className="min-w-0">
                                  <span className="font-medium">{item.keyword}</span>
                                  <span className="text-slate-400 ml-1 truncate block">
                                    → {item.anchor_link}
                                    {item.anchor_text && ` (${item.anchor_text})`}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}

                    {/* Errors */}
                    {bulkError && (
                      <div className="rounded-lg border border-red-200 p-3 bg-red-50">
                        <p className="text-xs font-medium text-red-600 mb-1">⚠️ Error format:</p>
                        <pre className="text-xs text-red-500 whitespace-pre-wrap">{bulkError}</pre>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <Separator />

                {/* Options Grid (shared) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Language */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Bahasa
                    </Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger data-testid="language-select" className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Indonesia">Indonesia</SelectItem>
                        <SelectItem value="English">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Writing Style */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <PenTool className="w-4 h-4" />
                      Gaya
                    </Label>
                    <Select value={writingStyle} onValueChange={setWritingStyle}>
                      <SelectTrigger data-testid="writing-style-select" className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blog-friendly">Blog</SelectItem>
                        <SelectItem value="academic">Akademik</SelectItem>
                        <SelectItem value="journalistic">Jurnalistik</SelectItem>
                        <SelectItem value="conversational">Conversational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tone */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Tone
                    </Label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger data-testid="tone-select" className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Profesional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Progress */}
                {currentJob && currentJob.status === "processing" && (
                  <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-700">
                        {currentJob.is_bulk
                          ? `Generating: "${currentJob.current_keyword || '...'}"`
                          : "Generating artikel..."}
                      </span>
                      <span className="text-sm text-blue-600">
                        {currentJob.articles_generated}/{currentJob.article_count}
                      </span>
                    </div>
                    <Progress value={currentJob.progress} className="h-2" />
                    {currentJob.is_bulk && (
                      <p className="text-xs text-blue-500">
                        Item {(currentJob.current_item_index ?? 0) + 1} dari {currentJob.article_count}
                      </p>
                    )}
                  </div>
                )}

                {/* Generate Button */}
                <Button 
                  data-testid="generate-button"
                  onClick={mode === "bulk" ? handleBulkGenerate : handleGenerate}
                  disabled={isGenerating || (mode === "bulk" && bulkItems.length === 0)}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm hover:shadow-md transition-all"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : mode === "bulk" ? (
                    <>
                      <List className="w-5 h-5 mr-2" />
                      Bulk Generate {bulkItems.length > 0 ? `(${bulkItems.length} artikel)` : ""}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Artikel
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - History & Preview */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* History Section */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Archive className="w-5 h-5 text-slate-600" />
                      History Artikel
                    </CardTitle>
                    <CardDescription>
                      Daftar artikel yang sudah digenerate
                    </CardDescription>
                  </div>
                  
                  {articles.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleSelectAll}
                        data-testid="select-all-button"
                      >
                        {selectedArticleIds.length === articles.length ? "Batal Pilih" : "Pilih Semua"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleExportWordPress}
                        data-testid="export-wordpress-button"
                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                      >
                        <FileCode className="w-4 h-4 mr-1" />
                        WordPress {selectedArticleIds.length > 0 ? `(${selectedArticleIds.length})` : `(${articles.length})`}
                      </Button>
                      {selectedArticleIds.length > 0 && (
                        <>
                          <Button
                            size="sm"
                            onClick={handleDownloadZip}
                            data-testid="download-zip-button"
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            ZIP ({selectedArticleIds.length})
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={handleDeleteBulk}
                            data-testid="delete-bulk-button"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Hapus ({selectedArticleIds.length})
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {articles.length === 0 ? (
                  <div className="empty-state py-12">
                    <FileText className="w-12 h-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">Belum ada artikel</p>
                    <p className="text-sm text-slate-400">Generate artikel pertama Anda</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="space-y-3">
                      {articles.map((article, index) => (
                        <div 
                          key={article.id}
                          className={`history-item animate-fade-in-up ${
                            selectedArticle?.id === article.id ? 'selected' : ''
                          }`}
                          style={{ animationDelay: `${index * 50}ms` }}
                          data-testid={`article-item-${article.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedArticleIds.includes(article.id)}
                              onCheckedChange={() => toggleArticleSelection(article.id)}
                              data-testid={`article-checkbox-${article.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-slate-900 truncate">
                                {article.title}
                              </h4>
                              <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                <span>{article.keyword}</span>
                                <span>•</span>
                                <span>{article.word_count} kata</span>
                                <span>•</span>
                                <span>{article.language}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewArticle(article.id)}
                                data-testid={`view-article-${article.id}`}
                              >
                                <Eye className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadArticle(article.id)}
                                data-testid={`download-article-${article.id}`}
                              >
                                <Download className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteArticle(article.id)}
                                data-testid={`delete-article-${article.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Preview Section */}
            {showPreview && selectedArticle && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Preview Artikel</CardTitle>
                      <CardDescription>{selectedArticle.keyword}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        {selectedArticle.word_count} kata
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadArticle(selectedArticle.id)}
                        data-testid="preview-download-button"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download HTML
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowPreview(false);
                          setSelectedArticle(null);
                        }}
                        data-testid="close-preview-button"
                      >
                        Tutup
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <div 
                      className="article-content prose prose-slate max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                    />
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
